import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPersonDigging, faSkull, faBan } from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { compareVersionsDesc, isReleaseCandidate } from "./version";
import {ReleaseNote} from "./types/api";

export type GroupedReleaseNotes = Record<string, ReleaseNote[]>;

// Icons shown for known version-status flags.
const FLAG_ICONS: Record<string, IconDefinition> = {
    RC: faPersonDigging,
    EOL: faSkull,
    BROKEN: faBan,
};

// Badge label for a release-note flag, e.g. "(RC)" with a status icon.
// Returns null when there is no flag so callers can skip rendering.
export function flagBadge(flag?: string): React.ReactNode | null {
    if (!flag) return null;
    const icon = FLAG_ICONS[flag.toUpperCase()];
    return icon
        ? <><FontAwesomeIcon icon={icon} /> ({flag})</>
        : `(${flag})`;
}

// Effective flag of a release: an explicit flag wins; otherwise an RC version
// suffix in the tag (e.g. 16.0.0-RC.1) implies the RC flag.
export function effectiveFlag(flag?: string, tagName?: string): string | undefined {
    if (flag) return flag;
    return isReleaseCandidate(tagName) ? "RC" : undefined;
}

export function groupReleaseNotesByMajor(notes: ReleaseNote[]): GroupedReleaseNotes {
    const groups: GroupedReleaseNotes = {};
    for (const rn of notes) {
        const majorKey = `${rn.tag_name.split(".")[0]}.x`;
        (groups[majorKey] ??= []).push(rn);
    }
    for (const key of Object.keys(groups)) {
        groups[key].sort((a, b) => compareVersionsDesc(a.tag_name, b.tag_name));
    }
    return groups;
}

// Helper to parse ISO or legacy release date formats
export function parseReleaseDate(input?: string): Date | null {
    if (!input) return null;

    // ISO format works natively
    const iso = new Date(input);
    if (!isNaN(iso.getTime())) {
        return iso;
    }

    // legacy format: dd-MM-yyyy HH:mm:ss Z
    const match = input.match(/(\d{2})-(\d{2})-(\d{4}) (\d{2}):(\d{2}):(\d{2}) ([+-]\d{4})/);
    if (!match) return null;

    const [, dd, MM, yyyy, hh, mm, ss, tz] = match;

    const isoLike = `${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}${tz}`;
    const parsed = new Date(isoLike);

    return isNaN(parsed.getTime()) ? null : parsed;
}